import type { SOPWarning } from '@/data/mockSignBuilderCatalog'
import type {
  DeliverableEntry,
  Request,
  Single,
  Subject,
  AssigneeLogEntry,
  ChatMessage,
} from '@/lib/dd-queue/types'
import type {
  ProofVersion,
  DesignFile,
  PimRecord,
  SentBackState,
  SubjectMutation,
  ChangeIntent,
} from '@/lib/dd-queue/types/subjectMutation'
import type { PimCustomFieldValue } from '@/types/origin'
import { readSubjectMutation } from '@/lib/dd-queue/subjectMutationKey'
import { getActivePhase, resolveOpenDeliverables } from '@/lib/dd-queue/workflow'
import { triageAnchor } from '@/lib/dd-queue/turnaround'
import { applyBuildTbdResolution } from '@/lib/dd-queue/buildTbdFlag'
import type { LinePart, LinePartsSource } from '@/lib/dd-queue/lineParts'
import {
  salesWaitIntervals,
  salesWaitStats,
  type SalesWaitInterval,
} from '@/lib/dd-queue/salesWait'

export interface ResolvedSubject {
  sku: string | null
  name: string | null
  productType: 'custom' | 'standard'
  deliverables: Record<string, DeliverableEntry>
  deletedAt: string | null
  deletedBy: string | null
  estimationMode: Subject['estimationMode']
  age: number
  isComplete: boolean
  /** Unix ms anchor for turnaround — reference ResolvedSubject.triageEnteredAt */
  triageEnteredAt: number
  isBundle?: boolean
  bundleDelta?: Subject['bundleDelta']
  /** Total non-bundle members on a sellable bundle master; 0 otherwise. */
  bundleMemberCount: number
  /** Members with SKU complete/not_required (or deleted). */
  bundleReadyCount: number
  /** Bundle line held until members are SKU-ready (unless bundleReleased). */
  bundleWaiting: boolean
  flags: string[]
  sopWarnings: SOPWarning[]
  /** Peek / full-view session fields — from SubjectMutation + seed (reference ResolvedSubject). */
  activityHead: { t: string; e: string }[]
  deliverableOpenedAt?: Record<string, number>
  deliverableCompletedAt?: Record<string, number>
  deliverableAssigneeLogs?: Record<string, AssigneeLogEntry[]>
  designTimerMs: number
  designTimerStartedAt: number | null
  proofVersions: ProofVersion[]
  quotedProofId: string | null
  /** F-145 — set while the line sits on a step it was sent back to (optional: test stubs predate it). */
  sentBack?: SentBackState | null
  filesByCategory: Record<string, DesignFile[]>
  qty: number
  spec?: Subject['spec']
  pimMode: Subject['pimMode']
  installRequired: boolean
  modify?: Subject['modify']
  attributes: Record<string, string>
  /** DD-ATTR-PRECEDENCE-001 — keys with a source are never intake-gap-filled. */
  attributeSources: Record<string, 'intake' | 'human'>
  /** F-099 — stored parts list ([] when none) and who wrote it last (null = never stored). */
  parts: LinePart[]
  partsSource: LinePartsSource | null
  /** F-144 — the product's PIM record at intake (update requests), read-only; null = none captured. */
  pimRecord?: PimRecord | null
  /** F-144 (slice 3a) — "What's changing" (marked items); null = Triage never set it. */
  changeIntents: ChangeIntent[] | null
  chat: ChatMessage[]
  /** Free-form notes painted on the proof-packet line page (mutation-only). */
  packetNotes: string
  /** PMS / spot colors for the proof-packet Colors section (mutation-only). */
  pmsColors: string[]
  /** Known UIN from intake / seed — packet Description block. */
  knownUin: string | null
  /** STD-CUSTOM-NCO-001 — rep-entered Standard-Custom values. */
  customFieldValues?: PimCustomFieldValue[]
  /** DD-SALESWAIT-004 — from mut.chat only (seed chat has no reliable ts). */
  awaitingSalesReply: boolean
  awaitingSalesSince: number | null
  totalAwaitingSalesMs: number
  /** DD-DWELL-PAUSE-001 — sales-wait intervals for excludeIntervals. */
  salesWaitIntervals: SalesWaitInterval[]
}

function mergeDeliverables(
  seed: Record<string, DeliverableEntry>,
  updates: Record<string, Partial<DeliverableEntry>> | undefined
): Record<string, DeliverableEntry> {
  if (!updates) return seed
  const result = { ...seed }
  for (const [key, update] of Object.entries(updates)) {
    result[key] = { ...(result[key] ?? { status: 'pending', assignee: null }), ...update }
  }
  return result
}

/**
 * Merge seed + session mutation into a ResolvedSubject.
 * Identity: `lineKey` selects the child / single. Display: `ResolvedSubject.sku` from seed.
 */
export function resolveSubject(
  req: Request,
  lineKey: string,
  mutations: Record<string, SubjectMutation>
): ResolvedSubject | null {
  let seed: Subject | Single | undefined
  if (req.isMaster) {
    seed = req.children.find((c) => c.lineKey === lineKey)
  } else {
    seed = (req as Single).lineKey === lineKey ? req : undefined
  }
  if (!seed?.lineKey) return null

  const mut = readSubjectMutation(mutations, req.id, lineKey)

  const merged = mergeDeliverables(seed.deliverables, mut.deliverableUpdates)
  // NP-ROUTING-FREEZE-FIX-001 — submission SoT wins when known (hydrate + freeze).
  const estimationMode =
    req.submissionEstimationMode ?? mut.estimationMode ?? seed.estimationMode
  const resolved = resolveOpenDeliverables(merged, { estimationMode })

  // Bundle gate — reference queue-store.ts resolveSubject: hold open steps until
  // non-bundle members are SKU-ready (unless mut.bundleReleased).
  const isBundle = !!seed.isBundle
  let bundleMemberCount = 0
  let bundleReadyCount = 0
  let bundleWaiting = false
  if (isBundle) {
    const prog = bundleMembersReady(req, seed.lineKey, mutations)
    bundleMemberCount = prog.total
    bundleReadyCount = prog.ready
    if (!mut.bundleReleased && !prog.allReady) {
      bundleWaiting = true
      for (const k of Object.keys(resolved)) {
        if (resolved[k].status === 'open') {
          resolved[k] = { ...resolved[k], status: 'pending' }
        }
      }
    }
  }

  const waitChat = mut.chat ?? []
  const waitReplies = mut.chatReplies ?? {}
  const waitStats = salesWaitStats(waitChat, waitReplies)
  const waitIntervals = salesWaitIntervals(waitChat, waitReplies)

  return {
    sku: seed.sku,
    name: mut.nameOverride ?? ('product' in seed ? seed.product : seed.name) ?? null,
    productType: seed.productType ?? 'custom',
    deliverables: resolved,
    deletedAt: mut.deletedAt ?? null,
    deletedBy: mut.deletedBy ?? null,
    estimationMode,
    age: seed.age,
    triageEnteredAt: triageAnchor(seed),
    isComplete: getActivePhase(resolved) === 'complete',
    isBundle: seed.isBundle,
    bundleDelta: seed.bundleDelta,
    bundleMemberCount,
    bundleReadyCount,
    bundleWaiting,
    // F-057: the spec-derived "Build TBD" flag clears once a human recorded a substrate
    // (F-120: or a parts list whose manufactured parts all have one).
    flags: applyBuildTbdResolution('flags' in seed ? seed.flags : [], mut.attributes, mut.parts),
    sopWarnings: seed.sopWarnings ?? [],
    activityHead: mut.activityHead ?? [],
    deliverableOpenedAt: mut.deliverableOpenedAt,
    deliverableCompletedAt: mut.deliverableCompletedAt,
    deliverableAssigneeLogs: mut.deliverableAssigneeLogs,
    designTimerMs: mut.stepTimers?.design?.ms ?? mut.designTimerMs ?? seed.designTime * 1000,
    designTimerStartedAt:
      mut.stepTimers?.design?.startedAt !== undefined
        ? mut.stepTimers.design.startedAt
        : (mut.designTimerStartedAt ?? null),
    proofVersions: mut.proofVersions ?? [],
    quotedProofId: mut.quotedProofId ?? null,
    sentBack: mut.sentBack ?? null,
    filesByCategory: mut.filesByCategory ?? {},
    qty: seed.qty ?? 1,
    spec: seed.spec,
    pimMode: mut.pimMode ?? seed.pimMode,
    installRequired: mut.installRequired ?? seed.installRequired,
    modify: seed.modify,
    attributes: mut.attributes ?? {},
    attributeSources: mut.attributeSources ?? {},
    parts: mut.parts ?? [],
    partsSource: mut.partsSource ?? null,
    pimRecord: mut.pimRecord ?? null,
    changeIntents: mut.changeIntents ?? null,
    customFieldValues: 'customFieldValues' in seed ? seed.customFieldValues : undefined,
    chat: [...(seed.chat ?? []), ...(mut.chat ?? [])].map((m) => ({
      ...m,
      replies: [...(m.replies ?? []), ...(mut.chatReplies?.[m.id] ?? [])],
    })),
    packetNotes: mut.packetNotes ?? '',
    pmsColors: mut.pmsColors ?? [],
    knownUin: ('knownUin' in seed ? seed.knownUin : null) ?? null,
    awaitingSalesReply: waitStats.awaitingSalesReply,
    awaitingSalesSince: waitStats.awaitingSalesSince,
    totalAwaitingSalesMs: waitStats.totalAwaitingSalesMs,
    salesWaitIntervals: waitIntervals,
  }
}

/**
 * Reference queue-store.ts bundleMembersReady — members of a sellable bundle
 * excluding the synthetic BNDL line. Deleted or SKU complete/not_required count ready.
 */
function bundleMembersReady(
  req: Request,
  bundleLineKey: string,
  mutations: Record<string, SubjectMutation>
): { total: number; ready: number; allReady: boolean } {
  if (!req.isMaster) return { total: 0, ready: 0, allReady: true }
  const members = req.children.filter(
    (c) => !!c.lineKey && c.lineKey !== bundleLineKey && !c.isBundle
  )
  let ready = 0
  for (const m of members) {
    const r = resolveSubject(req, m.lineKey!, mutations)
    if (!r || r.deletedAt) {
      ready++
      continue
    }
    const skuStatus = r.deliverables.sku?.status
    if (skuStatus === 'complete' || skuStatus === 'not_required') ready++
  }
  return { total: members.length, ready, allReady: ready === members.length }
}

export function isSubjectComplete(
  req: Request,
  lineKey: string,
  mutations: Record<string, SubjectMutation>
): boolean {
  const resolved = resolveSubject(req, lineKey, mutations)
  if (!resolved) return false
  return getActivePhase(resolved.deliverables) === 'complete'
}
